import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  DONATION_FUNDS,
  RECURRING_GIVING_FREQUENCIES,
  type DonationFund,
  type RecurringGivingFrequency,
} from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { publicGivingApi } from '@/services/giving.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';
import { LocalePicker } from '@/components/locale-picker';
import { LogoMark } from '@/components/logo-mark';

const PRESET_AMOUNTS = [25, 50, 100, 250];

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/**
 * The public, unauthenticated giving page — a donor gives with no account,
 * the same way Pushpay or Tithe.ly work. Reached at `/give/:tenantSlug`,
 * outside both the authenticated and unauthenticated app shells: whoever
 * opens the link sees this regardless of their own session state.
 */
export function GivePage() {
  const { tenantSlug = '' } = useParams();
  const { t } = useI18n();

  const [fund, setFund] = useState<DonationFund>('GENERAL');
  const [amountInput, setAmountInput] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState<RecurringGivingFrequency>('MONTHLY');
  const [error, setError] = useState<string | null>(null);
  const [thankYou, setThankYou] = useState(false);
  const [recurringManageUrl, setRecurringManageUrl] = useState<string | null>(null);

  const tenant = useQuery({
    queryKey: ['giving', 'public', 'tenant', tenantSlug],
    queryFn: ({ signal }) => publicGivingApi.getTenantInfo(tenantSlug, signal),
  });

  const checkout = useMutation({
    mutationFn: () =>
      publicGivingApi.checkout(tenantSlug, {
        fund,
        amount: Math.round(Number.parseFloat(amountInput || '0') * 100),
        currency: tenant.data?.defaultCurrency ?? 'USD',
        donorName: donorName.trim() || null,
        donorEmail: donorEmail.trim() || null,
      }),
    onSuccess: (result) => {
      setError(null);
      if (result.status === 'SUCCEEDED') {
        setThankYou(true);
      } else if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        setError(t('errors.INTERNAL_ERROR'));
      }
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const createRecurring = useMutation({
    mutationFn: () =>
      publicGivingApi.createRecurring(tenantSlug, {
        fund,
        amount: Math.round(Number.parseFloat(amountInput || '0') * 100),
        currency: tenant.data?.defaultCurrency ?? 'USD',
        frequency,
        donorName: donorName.trim() || null,
        donorEmail: donorEmail.trim(),
      }),
    onSuccess: (result) => {
      setError(null);
      setRecurringManageUrl(result.manageUrl);
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const amountValid = Number.parseFloat(amountInput || '0') > 0;
  const donorEmailValid = donorEmail.trim().length > 0;
  const canSubmit = amountValid && (!isRecurring || donorEmailValid);

  if (thankYou) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <header className="auth-header">
            <LogoMark />
            <h1 className="auth-title">{t('giving.public.thankYouTitle')}</h1>
            <p className="auth-subtitle">{t('giving.public.thankYouBody')}</p>
          </header>
        </div>
      </main>
    );
  }

  if (recurringManageUrl) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <header className="auth-header">
            <LogoMark />
            <h1 className="auth-title">{t('giving.public.recurring.confirmedTitle')}</h1>
            <p className="auth-subtitle">{t('giving.public.recurring.confirmedBody')}</p>
          </header>
          <a className="auth-secondary-link" href={recurringManageUrl}>
            {t('giving.public.recurring.manageTitle')}
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <header className="auth-header">
          <LogoMark />
          <h1 className="auth-title">
            {tenant.data ? t('giving.public.title', { values: { name: tenant.data.name } }) : ' '}
          </h1>
          <p className="auth-subtitle">{t('giving.public.subtitle')}</p>
        </header>

        {tenant.isError && <Alert tone="danger">{errorMessage(tenant.error, t)}</Alert>}
        {error && <Alert tone="danger">{error}</Alert>}

        {tenant.isSuccess && (
          <form
            className="auth-form"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              if (!canSubmit) return;
              if (isRecurring) createRecurring.mutate();
              else checkout.mutate();
            }}
          >
            <div className="field">
              <label className="field-label" htmlFor="give-fund">
                {t('giving.public.fund')}
              </label>
              <select
                id="give-fund"
                className="field-input"
                value={fund}
                onChange={(event) => setFund(event.target.value as DonationFund)}
              >
                {(tenant.data.funds.length > 0 ? tenant.data.funds : DONATION_FUNDS).map(
                  (value) => (
                    <option key={value} value={value}>
                      {t(`giving.fund.${value}`)}
                    </option>
                  ),
                )}
              </select>
            </div>

            <div className="amount-presets">
              {PRESET_AMOUNTS.map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  variant="ghost"
                  onClick={() => setAmountInput(String(preset))}
                >
                  {preset}
                </Button>
              ))}
            </div>

            <Field
              label={t('giving.public.amount', {
                values: { currency: tenant.data.defaultCurrency },
              })}
              name="give-amount"
              type="number"
              min="1"
              step="0.01"
              value={amountInput}
              onChange={(event) => setAmountInput(event.target.value)}
              required
            />

            <label className="checkbox">
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={(event) => setIsRecurring(event.target.checked)}
              />
              {t('giving.public.recurring.toggle')}
            </label>

            {isRecurring && (
              <div className="field">
                <label className="field-label" htmlFor="give-frequency">
                  {t('giving.public.recurring.frequency')}
                </label>
                <select
                  id="give-frequency"
                  className="field-input"
                  value={frequency}
                  onChange={(event) => setFrequency(event.target.value as RecurringGivingFrequency)}
                >
                  {RECURRING_GIVING_FREQUENCIES.map((value) => (
                    <option key={value} value={value}>
                      {t(`giving.public.recurring.frequencyOption.${value}`)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <Field
              label={t('giving.public.donorName')}
              name="give-donor-name"
              value={donorName}
              onChange={(event) => setDonorName(event.target.value)}
            />

            <Field
              label={
                isRecurring ? t('giving.public.donorEmailRequired') : t('giving.public.donorEmail')
              }
              name="give-donor-email"
              type="email"
              value={donorEmail}
              onChange={(event) => setDonorEmail(event.target.value)}
              required={isRecurring}
            />

            <Button
              type="submit"
              variant="primary"
              loading={checkout.isPending || createRecurring.isPending}
              disabled={!canSubmit}
            >
              {isRecurring ? t('giving.public.recurring.start') : t('giving.public.give')}
            </Button>
          </form>
        )}

        <footer className="auth-footer">
          <LocalePicker />
        </footer>
      </div>
    </main>
  );
}
